import { withAITracking } from "@microsoft/applicationinsights-react-js";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Trans } from "react-i18next";
import { RouteComponentProps } from "react-router";

import logoSmall from "/asset/image/logo-small.svg";
import EmptyStateImage from "/asset/image/secureMessagingNoMessages.png";
import { Avatar, Divider, Link, Loading, Text } from "/component/base";
import EditableMessage from "/component/base/EditableMessage";
import MainContent from "/component/layout/MainContent";
import ReplyMessageModal from "/component/modal/ReplyMessageModal";
import EmptyStateCta from "/component/partial/EmptyStateCta";
import MessageAttachmentsList from "/component/partial/MessageAttachmentsList";
import { useAmwell, useAmwellMessageDetails, useTranslation } from "/hook";
import { layout } from "/styles";
import { date, formatText } from "/util";
import { reactPlugin } from "/util/appInsights.util";

import { useMessageQuery } from "./useMessageQuery";

const IMAGE_SIZE = 274;

const Message = ({ match }: RouteComponentProps<{ id: string }>) => {
  const { t } = useTranslation("message");
  const messageId = match.params.id;

  const { consumer, sdk } = useAmwell();
  const { data, error, loading } = useAmwellMessageDetails({
    messageId,
    inbox: !(match.path.indexOf("/messages/sent/") > 0),
  });
  const { messageDetails } = data || {};
  const isInitialLoad = (loading && !messageDetails) || (!error && !messageDetails);

  const [showReply, setShowReply] = useState(false);
  const handleCancelReply = () => setShowReply(false);

  const { contactByName } = useMessageQuery();
  const aftercarePhoneNumber = contactByName("cityMdAftercare")?.phoneNumber;
  const aftercarePhoneNumberFormatted =
    aftercarePhoneNumber && formatText.parsePhone(aftercarePhoneNumber);
  const callCenterPhoneNumber = contactByName("summitCallCenter")?.phoneNumber;
  const callCenterPhoneNumberFormatted =
    callCenterPhoneNumber && formatText.parsePhone(callCenterPhoneNumber);

  useEffect(() => {
    if (consumer && messageDetails) {
      try {
        sdk?.secureMessageService.updateMessageRead(consumer, messageDetails);
      } catch (e: any) {
        // We don't need to alert the user to this error, we can just try
        // again next time they view this message.
        console.error("Unable to mark message as read", e);
      }
    }
  }, [consumer, messageDetails]);

  return (
    <MainContent parentPage="account" pageTitle={messageDetails ? messageDetails.subject : ""}>
      <Helmet>
        <title>{messageDetails ? messageDetails.subject : t("pageTitle")}</title>
      </Helmet>

      {isInitialLoad && <Loading />}

      {!isInitialLoad && error && (
        <EmptyStateCta
          imageHeight={IMAGE_SIZE}
          imageWidth={IMAGE_SIZE}
          imageSource={EmptyStateImage}
          subtitle={t("alert.fetchError.subtitle")}
          title={t("alert.fetchError.title")}
        />
      )}

      {!isInitialLoad && !error && messageDetails && (
        <article>
          <section css={[layout.padding("none", "none", "standard"), layout.flexLTR]}>
            <Avatar alt={"CityMD Clinical Staff"} src={logoSmall} />

            <div css={[layout.flexVertical, layout.padding("none", "none", "none", "standard")]}>
              <Text variant="body1Bold">{"CityMD Clinical Staff"}</Text>

              <Text color="textSecondary" variant="body2">
                {`${date.formatDate(messageDetails.date, "LLL d, yyyy")} at ${date.formatDate(
                  messageDetails.date,
                  "h:mm aa ",
                )}`}
              </Text>
            </div>
          </section>

          <Divider css={layout.margin("skip", "skip", "expanded")} />

          <EditableMessage disabled html={messageDetails.body} />

          <MessageAttachmentsList attachments={messageDetails.attachments} />

          <aside
            css={[layout.padding("expanded", "none"), layout.spacedChildrenVertical("condensed")]}
          >
            <Text color="textSecondary" element="p" variant="body2">
              <Trans
                components={{
                  aftercareAnchor: (
                    <Link.Anchor href={`tel:${aftercarePhoneNumber}`} variant="tertiary" />
                  ),
                  callCenterAnchor: (
                    <Link.Anchor href={`tel:${callCenterPhoneNumber}`} variant="tertiary" />
                  ),
                }}
                i18nKey="disclaimer.expandingService"
                t={t}
                values={{
                  aftercarePhoneNumber: aftercarePhoneNumberFormatted,
                  callCenterPhoneNumber: callCenterPhoneNumberFormatted,
                }}
              />
            </Text>
          </aside>
        </article>
      )}

      {messageDetails?.canReply && (
        <ReplyMessageModal
          close={handleCancelReply}
          isOpen={showReply}
          messageDetails={messageDetails}
        />
      )}
    </MainContent>
  );
};

export default withAITracking(reactPlugin, Message, undefined, "ai-tracking");
